/**
 * Calendar arithmetic for the monthly ledger (P4-T2).
 *
 * A business DATE is a strict `YYYY-MM-DD` string and a business MONTH is `YYYY-MM`, and
 * neither is ever a JavaScript `Date`: `Date` arithmetic reintroduces UTC conversion, and
 * the month boundary the ledger uses is a calendar boundary, not an instant. The month
 * maths below is therefore pure clock-free integer arithmetic, which is also what makes
 * the year boundary (`2026-12` → `2027-01`) and the February case testable without a
 * clock.
 *
 * "Today" is never computed here from a browser clock. It comes from the API's envelope
 * (`meta.businessToday`, in `America/Winnipeg`); `todayInTimeZone` from `@saldo/domain` is
 * the same rule the server applies, and it exists here only so the client can fall back to
 * a *month* before the first response arrives — never to decide whether a row is overdue.
 */

import { todayInTimeZone } from '@saldo/domain';

/** The business time zone (register §7; the same constant the API boots with). */
export const BUSINESS_TIME_ZONE = 'America/Winnipeg';

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Whether the value is a business MONTH (`YYYY-MM`). */
export function isMonth(value: string): boolean {
  return MONTH.test(value);
}

/**
 * The month `delta` months away from `month`.
 *
 * Integer arithmetic on a month index, so December→January and the leap-year February
 * are structural rather than special cases.
 */
export function shiftMonth(month: string, delta: number): string {
  const match = MONTH.exec(month);
  if (match === null) throw new Error(`not a YYYY-MM business month: ${month}`);
  const index = Number(match[1]) * 12 + (Number(match[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const monthNumber = (index % 12) + 1;
  return `${String(year).padStart(4, '0')}-${String(monthNumber).padStart(2, '0')}`;
}

/**
 * The half-open period a month selects: `[start, endExclusive)`.
 *
 * Half-open is the same convention `@saldo/domain`'s `openingSum`/`activitySum` use, so a
 * row belongs to exactly one month and an entry on the first of the next month is never
 * counted twice.
 */
export function monthBounds(month: string): { readonly start: string; readonly endExclusive: string } {
  if (!isMonth(month)) throw new Error(`not a YYYY-MM business month: ${month}`);
  return { start: `${month}-01`, endExclusive: `${shiftMonth(month, 1)}-01` };
}

/** The month a business date falls in. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/**
 * Today's month in the business time zone, from an instant.
 *
 * This is the *pre-response* fallback for the month the ledger opens on. Once the API has
 * answered, `meta.businessToday` is authoritative and this is not consulted again.
 */
export function businessMonth(timeZone: string, instant: Date): string {
  return monthOf(todayInTimeZone(timeZone, instant));
}

/** The month a business date's calendar label reads, e.g. `2026-10` → `October 2026`. */
export function monthLabel(month: string): string {
  if (!isMonth(month)) return month;
  const [year, number] = month.split('-');
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${names[Number(number) - 1] ?? number} ${year}`;
}
